/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { nanoid } from 'nanoid';
import { useAuth } from '../providers';

import { AuthName } from '../states/constants';
import { useVariables } from './useVariables';

export enum RequestMethods {
  post = 'post',
  get = 'get',
}

export type RequestData = {
  [key: string]: string | boolean;
};

export type RequestOptions = {
  method: RequestMethods;
  data?: any;
  headers?: {
    [key: string]: string | boolean | undefined;
  };
};

export function useRequest(baseUrl?: string) {
  const { apiUrl, apiClients, appScope, appScopeVersion } = useVariables();
  const { logout, handleRefreshToken } = useAuth();

  const requestClient = (url: string, options: RequestOptions) =>
    axios
      .post(`${apiClients}${url}`, options.data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(AuthName.token)}`,
        },
      })
      .then((response) => response.data)
      .catch((error) => {
        Sentry.captureException(error, {
          extra: { operation: 'request', url, options },
        });
        throw new Error(error);
      });

  const request = (url: string, options: RequestOptions) =>
    axios({
      method: options.method,
      url: `${baseUrl || apiUrl}${url}`,
      data: options.data,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${localStorage.getItem(AuthName.token)}`,
        'correlation-id': nanoid(),
        appscope: appScope as string,
        'appscope-version': appScopeVersion as string,
      },
    })
      .then((response) => response.data)
      .catch((error) => {
        Sentry.captureException(error, {
          extra: { operation: 'request', url, options },
        });
        if (error.response.status === 401) {
          return handleRefreshToken()
            .catch(() => {
              logout();
            })
            .then(async () => {
              const config = error.config;
              config.headers['Authorization'] = `Bearer ${localStorage.getItem(
                AuthName.token
              )}`;
              return axios.request(config).then((response) => response.data);
            });
        } else {
          throw new Error(error);
        }
      });

  return {
    request,
    requestClient,
  };
}
